import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { extractExcerpt } from '../utils/wordpressParser';
import './CommunityPages.css';

const CommunityPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/pages');
      setPages(response.data);
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Failed to load community pages');
    } finally {
      setLoading(false);
    }
  };

  const filteredPages = pages.filter(page => 
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (page.excerpt && page.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Group pages by parent-child relationships
  const pageHierarchy = () => {
    const parentPages = filteredPages.filter(page => !page.parentPage);
    const childPages = filteredPages.filter(page => page.parentPage);
    
    return parentPages.map(parent => ({
      ...parent,
      children: childPages.filter(child => child.parentPage === parent._id)
    }));
  };

  const handlePageClick = (page) => {
    navigate(`/community-pages/${page.slug}`, { state: { page } });
  };

  if (loading) {
    return (
      <div className="community-pages-container">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading community pages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="community-pages-container">
        <div className="error-container">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={fetchPages} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="community-pages-container">
      <div className="community-pages-header">
        <h1>📄 Community Pages</h1>
        <p>Browse community information, services, and resources</p>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search pages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredPages.length === 0 ? (
        <div className="no-pages">
          <h3>No pages found</h3>
          <p>No community pages match your search criteria.</p>
        </div>
      ) : (
        <div className="pages-grid">
          {pageHierarchy().map(page => (
            <div key={page._id} className="page-card">
              <div className="page-card-header" onClick={() => handlePageClick(page)}>
                <h3>{page.title}</h3>
                <div className="page-meta">
                  <span className="page-template">{page.template || 'default'}</span>
                  {page.isHomePage && <span className="home-badge">🏠 Home</span>}
                  {page.requiresAuth && <span className="auth-badge">🔒 Members</span>}
                </div>
              </div>
              
              {(page.excerpt || page.content) && (
                <p className="page-excerpt">
                  {page.excerpt || extractExcerpt(page.content, 120)}
                </p>
              )}
              
              <div className="page-actions">
                <button 
                  className="view-page-btn"
                  onClick={() => handlePageClick(page)}
                >
                  View Page
                </button>
                <span className="page-date">
                  Updated: {new Date(page.updatedAt).toLocaleDateString()}
                </span>
              </div>

              {/* Child pages */}
              {page.children && page.children.length > 0 && (
                <div className="child-pages">
                  <h4>Related Pages:</h4>
                  <div className="child-pages-list">
                    {page.children.map(child => (
                      <div 
                        key={child._id} 
                        className="child-page-item"
                        onClick={() => handlePageClick(child)}
                      >
                        <span className="child-icon">└──</span>
                        <span className="child-title">{child.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pages.length > 0 && (
        <div className="pages-summary">
          <p>
            Showing {filteredPages.length} of {pages.length} community pages
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default CommunityPages;